// store/slices/clients.slice.js

import { createSlice } from "@reduxjs/toolkit";
import {
  fetchAllClients,
  fetchClient,
  createClient,
  updateClient,
  removeClient
} from "../../api/clientsApi";

const clientsSlice = createSlice({
  name: "clients",
  initialState: {
    items: [],             // all clients
    current: null,         // single client in view
    selectedClientId: null,
    status: "idle",        // "idle", "loading", "succeeded", "failed"
    error: null
  },
  reducers: {
    clearCurrentClient(state) {
      state.current = null;
      state.selectedClientId = null; // Optionally also clear selected ID
    },
    // Action to set the "active" clientId
    selectClientId(state, action) {
      state.selectedClientId = action.payload;
    }
  },
  extraReducers: (builder) => {
    /**
     * fetchAllClients
     */
    builder
      .addCase(fetchAllClients.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAllClients.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload || [];
      })
      .addCase(fetchAllClients.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });

    /**
     * fetchClient
     */
    builder
      .addCase(fetchClient.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchClient.fulfilled, (state, action) => {
        state.status = "succeeded";
        // The full doc is returned, store it in `current` or wherever you prefer
        state.current = action.payload;

        // Update corresponding item in `state.items`
        const index = state.items.findIndex((client) => client.client_id === action.payload.client_id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(fetchClient.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });

    /**
     * createClient
     */
    builder
      .addCase(createClient.pending, (state) => {
        state.status = "loading";
      })
      .addCase(createClient.fulfilled, (state, action) => {
        state.status = "succeeded";
        const newClient = action.payload;

        if (!newClient.client_id) {
          newClient.client_id = `temp-${Date.now()}`;
        }
        state.items.push(newClient);
      })
      .addCase(createClient.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });

    /**
     * updateClient
     */
    builder
      .addCase(updateClient.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateClient.fulfilled, (state, action) => {
        state.status = "succeeded";
        // If needed, you can update `items` or set `current`:
        // e.g. find & replace the updated client in `state.items`.
        const updatedClient = action.payload;
        const index = state.items.findIndex((client) => client.client_id === updatedClient.client_id);
        if (index !== -1) {
          state.items[index] = updatedClient;
        }
        if (state.current?.client_id === updatedClient.client_id) {
          state.current = updatedClient;
        }
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });

    /**
     * removeClient
     */
    builder
      .addCase(removeClient.pending, (state) => {
        state.status = "loading";
      })
      .addCase(removeClient.fulfilled, (state, action) => {
        state.status = "succeeded";
        const clientId = action.payload?.client_id;
        state.items = state.items.filter((c) => c.client_id !== clientId);
        if (state.current?.client_id === clientId) {
          state.current = null;
        }
      })
      .addCase(removeClient.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });
  }
});

// ** Actions **
export const { clearCurrentClient, selectClientId } = clientsSlice.actions;

// ** Selectors **
export const selectAllClients = (state) => state.clients.items;
export const selectCurrentClient = (state) => state.clients.current;
export const selectSelectedClientId = (state) => state.clients.selectedClientId;
export const selectClientsLoading = (state) => state.clients.status === "loading";

// ** Reducer **
export default clientsSlice.reducer;
